<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick, defineProps } from "vue";
import {t} from "@/controllerLK/AvalLocalization";

const props = defineProps({
  text: {
    type: String,
    default: ""
  },
  maxHeight: {
    type: [Number, String],
    default: 200
  }
});

// Высота строки
const LINE_HEIGHT = 18;
// Состояние раскрытия текста новости
const isCollapsed = ref(true);
// Отображение кнопки скрытия/раскрытия если весь текст помещается в указанные размеры
const showButton = ref(false);
// Ref на элемент с текстом
const contentRef = ref(null);

// Рассчитываем количество строк, которые влезут в maxHeight
const maxLines = computed(() => {
  const maxHeightPx = typeof props.maxHeight === "number"
    ? props.maxHeight
    : parseInt(props.maxHeight);
  return Math.floor(maxHeightPx / LINE_HEIGHT);
});

// Стили для конпки
const buttonStyle = computed(() => {
  if (isCollapsed.value) {
    return {};
  } else {
    return {
      position: "relative",
      paddingLeft: 0,
      background: "transparent",
      float: "right"
    };
  }
});

// Стили для текста
const contentStyle = computed(() => {
  if (isCollapsed.value) {
    return {
      display: "-webkit-box",
      WebkitLineClamp: maxLines.value,
      WebkitBoxOrient: "vertical",
      overflow: "hidden"
    };
  }
  return {
    display: "block",
    overflow: "visible"
  };
});

// Проверяем помещается ли весь текст в элемент.
const checkContentHeight = () => {
  if (!contentRef.value) return;

  // Временно снимаем ограничение для правильного измерения высоты
  const wasCollapsed = isCollapsed.value;
  isCollapsed.value = false;

  // Используем nextTick чтобы дождаться обновления DOM
  nextTick(() => {
    if (!contentRef.value) return;

    const contentHeight = contentRef.value.scrollHeight;
    const maxHeightPx = maxLines.value * LINE_HEIGHT;

    showButton.value = contentHeight > maxHeightPx;
    isCollapsed.value = wasCollapsed;
  });
};

// Переключение видимости текста.
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};

onMounted(() => {
  checkContentHeight();
  window.addEventListener("resize", checkContentHeight);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkContentHeight);
});
</script>

<template>
  <div class="text-clamp">
    <div
      ref="contentRef"
      :style="contentStyle"
      class="text-clamp__content"
    >
      <slot>{{ text }}</slot>
      <button
        v-if="showButton"
        class="text-clamp__button"
        type="button"
        :style="buttonStyle"
        @click="toggleCollapse"
      >
        {{ isCollapsed ? t("text_clamp.expand") : t("text_clamp.collapse") }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.text-clamp {
  position: relative;
  width: 100%;
  color: var(--text-text-main);
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  text-align: justify;

  &__button {
    all: unset;
    position: absolute;
    right: 0;
    bottom: 0;
    height: 18px;
    padding-left: 80px;
    cursor: pointer;
    color: var(--text-text-on);
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.014) 7.81%,
      rgba(255, 255, 255, 0.152) 15.4%,
      rgba(255, 255, 255, 0.245) 22.4%,
      rgba(255, 255, 255, 0.483) 29.39%,
      #FFFFFF 39.98%,
      #FFFFFF 100%
    );

    &:hover {
      color: var(--text-text-blue);
    }
  }
}
</style>